import { useEffect } from 'react';
import { doc, getFirestore, onSnapshot } from 'firebase/firestore';
import { getIdTokenResult, signOut } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../../lib/firebase/init';
import type { User } from '../../types';
import { getRestaurantId } from '../../utils/restaurant';

export const useStaffSessionGuard = (user: User | null) => {
  useEffect(() => {
    if (!user) return;
    const cleanups: Array<() => void> = [];
    if (user.authMode === 'device_activation') {
      cleanups.push(onSnapshot(doc(getFirestore(auth.app), 'users', user.id), (snapshot) => {
        if (snapshot.data()?.activationStatus === 'revoked') void signOut(auth);
      }));
    }
    let stopped = false;
    let ownerSession = false;
    const verifyOwnerDevice = async () => {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser || stopped) return;
      try {
        const token = await getIdTokenResult(firebaseUser);
        ownerSession = token.claims.owner === true;
        if (!ownerSession) return;
        const callable = httpsCallable(getFunctions(auth.app), 'validateOwnerDevice');
        await callable({ restaurantId: getRestaurantId() });
      } catch (error) {
        const code = String((error as { code?: string })?.code || '');
        if (ownerSession && (code.includes('permission-denied') || code.includes('unauthenticated'))) await signOut(auth);
      }
    };
    void verifyOwnerDevice();
    const interval = window.setInterval(() => void verifyOwnerDevice(), 60_000);
    const onVisibility = () => { if (document.visibilityState === 'visible') void verifyOwnerDevice(); };
    document.addEventListener('visibilitychange', onVisibility);
    cleanups.push(() => window.clearInterval(interval), () => document.removeEventListener('visibilitychange', onVisibility));
    return () => { stopped = true; cleanups.forEach((cleanup) => cleanup()); };
  }, [user]);
};
